import { gsap } from 'gsap';
import React, {
  PropsWithChildren,
  ReactNode,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { shallow } from 'zustand/shallow';

import { Chip, Dropdown, Fade, Icon } from '@column/column-ui-kit';

import { Pagination, PopoverFilterEntry, Table } from '~/components';
import { LogoLoading } from '~/elements';
import { clientWrapper } from '~/repositories/client';
import { useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { TableBottomFade } from '~/styles';

export interface PaginationWrapperProps {
  tableId: string;
  className?: string;
  fetch: (object: any) => Promise<any>;
  fetchId?: string;
  fetchSearch?: (object: any, search: string) => Promise<any>;
  searchTooltip?: ReactNode;
  columns: any;
  filter?: PopoverFilterEntry[];
  action?: ReactNode;
  empty?: ReactNode;
  rowClick?: (row: any) => void;
}

export interface PaginationWrapperRefProps {
  fetch: () => void;
}

const Container = styled.div`
  position: relative;
`;

const Wrapper = styled.div`
  will-change: height;
  transform: translateZ(0);
  position: relative;
  z-index: 2;
`;

const StyledLoading = styled(LogoLoading)`
  top: 80px;
`;

const Entries = styled(Chip)`
  --chip-pale: ${({ theme }) => theme.secondary.blendToBackground(150)};

  svg {
    display: inline-block;
    vertical-align: top;
    margin: 0 0 0 -4px;
  }
`;

export const PaginationWrapper = forwardRef<PaginationWrapperRefProps, PropsWithChildren<PaginationWrapperProps>>(
  (props, ref) => {
    const { currentUser } = useSessionStore();
    const { pageSize: settingsLimit, update: updateTableConfig } = useTableConfig(props.tableId);
    const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const fadeRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(false);
    const [usedPagination, setUsedPagination] = useState<boolean>(false);
    const [list, setList] = useState<any>([]);
    const [customFilter, setCustomFilter] = useState<PopoverFilterEntry[]>(props?.filter ?? []);
    const [usedFilter, setUsedFilter] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [entriesOpen, setEntriesOpen] = useState<boolean>(false);

    const handleSearchSubmit = (value: string) => {
      if (props.fetchSearch) {
        setSearchTerm(value);
      }
    };

    const filterObject = useMemo(
      () =>
        Object.assign(
          {},
          ...customFilter
            .map((filter: PopoverFilterEntry) => {
              if (!filter.show) {
                return;
              }
              if (filter.type === 'date' && filter.active) {
                return {
                  [`${filter.id}.${filter?.typeActive ?? 'gt'}`]: new Date(filter.active).toISOString(),
                };
              }
              return {
                [filter.id]: filter.active,
              };
            })
            .filter(Boolean)
        ),
      [customFilter]
    );

    const useInlineLoading = useMemo(
      () =>
        searchTerm.length > 0 ||
        usedFilter ||
        usedPagination ||
        (filterObject && Object.keys(filterObject).length !== 0),
      [filterObject, usedPagination, searchTerm]
    );

    const handlePrev = () => {
      setUsedPagination(true);
      fetchData(undefined, list[0][props?.fetchId ?? 'id'], settingsLimit, () => {
        setCurrentPage(currentPage - 1);
      });
    };

    const handleNext = () => {
      setUsedPagination(true);
      fetchData(list[list.length - 1][props?.fetchId ?? 'id'], undefined, settingsLimit, () => {
        setCurrentPage(currentPage + 1);
      });
    };

    const fetchData = (startingAfter?: string, endingBefore?: string, limit?: number, callback?: () => void) => {
      if (!currentUser) {
        return;
      }

      setLoading(true);

      const handleResponse = (response: any) => {
        if (callback) {
          callback();
        }

        if (!startingAfter && !endingBefore) {
          setCurrentPage(1);
        }

        if (!startingAfter && endingBefore) {
          setHasMore(true);
        } else {
          setHasMore(response.hasMore);
        }

        setList(response.entries);

        setLoading(false);
        setUsedPagination(false);
      };

      setTimeout(() => {
        if (searchTerm.length > 0 && props.fetchSearch) {
          setSearchLoading(true);
          props
            .fetchSearch({ startingAfter, endingBefore, limit: limit ?? settingsLimit, ...filterObject }, searchTerm)
            .then(handleResponse)
            .finally(() => {
              setSearchLoading(false);
            });

          return;
        }

        clientWrapper(
          handleResponse,
          (e) => {
            if (e?.type === 'request_validation_error') {
              addDangerNotification({
                content: e.message,
              });

              setLoading(false);

              console.warn('PaginationWrapper.RequestValidationError', e);

              return;
            }
            console.error('PaginationWrapper', e);
          },
          props.fetch({ startingAfter, endingBefore, limit: limit ?? settingsLimit, ...filterObject })
        );
      }, 100);
    };

    useImperativeHandle(
      ref,
      () => ({
        fetch: fetchData,
      }),
      []
    );

    useEffect(
      () =>
        useSessionStore.subscribe(
          (state) => [state.isSandbox, state.isLoading, state.currentUser?.defaultPlatformId],
          () => {
            fetchData();
          },
          {
            fireImmediately: true,
            equalityFn: shallow,
          }
        ),
      [settingsLimit]
    );

    useEffect(() => {
      if (searchTerm.length > 0) {
        setUsedFilter(true);
      }
      fetchData();
    }, [searchTerm]);

    useEffect(() => {
      fetchData();
      if (filterObject && Object.keys(filterObject).length !== 0) {
        setUsedFilter(true);
      }
    }, [filterObject]);

    const handleOnExit = () => {
      gsap.set(wrapperRef.current, {
        height: fadeRef.current?.offsetHeight,
      });
    };

    const handleOnEnter = () => {
      gsap.to(wrapperRef.current, {
        height: fadeRef.current?.offsetHeight,
        duration: 0.4,
        clearProps: 'height',
      });
    };

    return (
      <Container>
        <Fade show={loading && !useInlineLoading} base={StyledLoading} />
        <Wrapper ref={wrapperRef}>
          <Fade show={!loading || useInlineLoading} ref={fadeRef} onExit={handleOnExit} onEnter={handleOnEnter}>
            {props.empty && list.length < 1 && !useInlineLoading ? (
              props.empty
            ) : (
              <>
                {props.children}
                <Table
                  className={props.className}
                  action={props.action}
                  onRowClick={props.rowClick}
                  columns={props.columns}
                  filter={customFilter}
                  onFilterChange={setCustomFilter}
                  data={list}
                  onSearchSubmit={props.fetchSearch ? handleSearchSubmit : undefined}
                  searchShowReset={searchTerm.length > 0}
                  searchTooltip={props.searchTooltip}
                  searchLoading={searchLoading}
                  isLoading={loading && useInlineLoading}
                />
              </>
            )}
          </Fade>
        </Wrapper>
        <TableBottomFade show={(!loading || useInlineLoading) && list.length >= 1} timeoutEnter={400}>
          <Dropdown
            options={[5, 10, 15, 20, 25, 50].map((entries: number) => ({
              label: entries.toString(),
              value: entries,
            }))}
            size="small"
            variant="muted"
            active={settingsLimit}
            customLabel={
              <Entries counter={settingsLimit}>{entriesOpen ? <Icon.ChevronUp /> : <Icon.ChevronDown />}</Entries>
            }
            onOpenChange={setEntriesOpen}
            onChange={(value: number) => {
              updateTableConfig({ limit: value }).catch(() => {
                addDangerNotification({ content: 'Failed to save page size preference' });
              });
              fetchData(undefined, undefined, value);
            }}
          />
          <Pagination
            disablePrev={currentPage === 1}
            disableNext={!hasMore}
            current={currentPage}
            onPrev={handlePrev}
            onNext={handleNext}
          />
        </TableBottomFade>
      </Container>
    );
  }
);
